
import { getCookie } from "@/lib/auth/cookies";
import { BackgroundJob } from "@/types/BackgroundJob";
import { OptionalParamsBackgroundJobs } from "@/types/OptionalParamsBackgroundJobs";
import {
  submitBackgroundJob as submitBackgroundJobApi,
  submitBackgroundJobCompany as submitBackgroundJobCompanyApi,
  getBackgroundJobStatus as getBackgroundJobStatusApi,
  getBackgroundJobStatusCompany as getBackgroundJobStatusCompanyApi,
  deleteBackgroundJob as deleteBackgroundJobApi,
  deleteBackgroundJobCompany as deleteBackgroundJobCompanyApi,
  cancelBackgroundJob as cancelBackgroundJobApi,
  cancelBackgroundJobCompany as cancelBackgroundJobCompanyApi,
  type SubmitBackgroundJobOptions,
} from "@/lib/api/clients/BackgroundJobClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { isValidObjectId } from "@/lib/utils/input-validator";
import { Filter } from "@/types/Filter";
import { Guest } from "@/types/Guest";
import { sanitizeGuest } from "@/lib/utils/sanitizer";
import type { BulkEditableUpdates } from "@/types/BulkSelection";
import {
  SEATING_COLOUR_BY,
  SEATING_DETAIL_PAGES,
  SEATING_GUEST_FIELDS_MAX,
  SEATING_MODES,
  SEATING_ORIENTATIONS,
  SEATING_PAPER_SIZES,
} from "@/lib/seating/export-options";

/**
 * True when at least one key of BulkEditableUpdates is set. Treats
 * additionalFields specially — an empty `{}` should NOT count as an update.
 * Mirrors the backend's `hasAnyBulkUpdate` predicate so frontend and
 * backend agree on what "has any update" means.
 */
function isNonEmptyUpdates(u: BulkEditableUpdates): boolean {
  for (const k of Object.keys(u) as (keyof BulkEditableUpdates)[]) {
    const v = u[k];
    if (v === undefined) continue;
    if (k === "additionalFields") {
      if (v && typeof v === "object" && Object.keys(v).length > 0) return true;
    } else if (k === "clearFields") {
      // A clear-only edit IS a change, but only if the array is non-empty.
      if (Array.isArray(v) && v.length > 0) return true;
    } else {
      return true;
    }
  }
  return false;
}

export async function submitBackgroundJob(
  eventId: string,
  taskType: string,
  optionalParams: OptionalParamsBackgroundJobs,
  // Optional: a response-header callback (the idempotent-replay marker). Every
  // existing call site passes nothing and sees nothing new.
  opts?: SubmitBackgroundJobOptions,
): Promise<string | ServerError> {
  // Set right before the request goes out: a throw before it is the client's
  // own refusal (`local`), never a request whose answer was lost — the SMS
  // send dialog says "status unclear" only for the latter (audit r1 C9).
  let onTheWire = false;
  try {
    if (!taskType || taskType.trim() === "") {
      throw new Error("Task type is required");
    }
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid eventId");
    }
    // Submit idempotency key (any task type) — the backend dedupes by it, so
    // it must reach the wire exactly as minted. Refuse anything outside the
    // key alphabet here rather than let the backend 400 the whole submit.
    if (
      optionalParams.clientRequestId !== undefined &&
      (typeof optionalParams.clientRequestId !== "string" ||
        !/^[A-Za-z0-9._:-]{1,64}$/.test(optionalParams.clientRequestId))
    ) {
      throw new Error("validationError");
    }

    let uniqueGuestIds: string[] | undefined;
    let sanitizedGuests: Guest[] | undefined;

    if (
      taskType === "EXPORT_SPECIFIC_GUESTS_TO_EXCEL" ||
      taskType === "EXPORT_SPECIFIC_GUESTS_TO_CSV"
    ) {
      // Validate guestIds
      if (!Array.isArray(optionalParams.guestIds))
        throw new Error("validationError");
      if (optionalParams.guestIds.length === 0)
        throw new Error("validationError");
      if (optionalParams.guestIds.length > 10000)
        throw new Error("validationError");

      // Validate each guest ID
      for (const guestId of optionalParams.guestIds) {
        if (!isValidObjectId(guestId)) {
          throw new Error("validationError");
        }
      }

      // Remove duplicates
      uniqueGuestIds = [...new Set(optionalParams.guestIds)];
      validateExportOptions(optionalParams.fields, optionalParams.filters);
      validateSortGroupOptions(
        optionalParams.sortBy,
        optionalParams.sortOrder,
        optionalParams.gridGroupBy,
        optionalParams.groupByMainGuest,
      );
      optionalParams.guestIds = uniqueGuestIds;
    } else if (
      taskType === "EXPORT_GUESTS_TO_EXCEL" ||
      taskType === "EXPORT_GUESTS_TO_CSV"
    ) {
      validateExportOptions(optionalParams.fields, optionalParams.filters);
      validateSortGroupOptions(
        optionalParams.sortBy,
        optionalParams.sortOrder,
        optionalParams.gridGroupBy,
        optionalParams.groupByMainGuest,
      );
    } else if (
      taskType === "EXPORT_CHECK_IN_LOGS_TO_EXCEL" ||
      taskType === "EXPORT_CHECK_IN_LOGS_TO_CSV"
    ) {
      validateExportOptions(optionalParams.fields, optionalParams.filters);
      validateSortGroupOptions(
        optionalParams.sortBy,
        optionalParams.sortOrder,
        optionalParams.gridGroupBy,
        optionalParams.groupByMainGuest,
      );
    } else if (
      taskType === "EXPORT_EMAILS_SENT_TO_EXCEL" ||
      taskType === "EXPORT_EMAILS_SENT_TO_CSV"
    ) {
      validateExportOptions(optionalParams.fields, optionalParams.filters);
      validateSortGroupOptions(
        optionalParams.sortBy,
        optionalParams.sortOrder,
        optionalParams.gridGroupBy,
        optionalParams.groupByMainGuest,
      );
    } else if (
      taskType === "EXPORT_SPECIFIC_EMAILS_SENT_TO_EXCEL" ||
      taskType === "EXPORT_SPECIFIC_EMAILS_SENT_TO_CSV"
    ) {
      if (!Array.isArray(optionalParams.emailSentIds))
        throw new Error("validationError");
      if (optionalParams.emailSentIds.length === 0)
        throw new Error("validationError");
      if (optionalParams.emailSentIds.length > 10000)
        throw new Error("validationError");
      for (const emailSentId of optionalParams.emailSentIds) {
        if (!isValidObjectId(emailSentId)) {
          throw new Error("validationError");
        }
      }
      optionalParams.emailSentIds = [...new Set(optionalParams.emailSentIds)];
      validateExportOptions(optionalParams.fields, optionalParams.filters);
      validateSortGroupOptions(
        optionalParams.sortBy,
        optionalParams.sortOrder,
        optionalParams.gridGroupBy,
        optionalParams.groupByMainGuest,
      );
    } else if (
      taskType === "EXPORT_REQUESTS_TO_EXCEL" ||
      taskType === "EXPORT_REQUESTS_TO_CSV"
    ) {
      validateExportOptions(optionalParams.fields, optionalParams.filters);
      validateSortGroupOptions(
        optionalParams.sortBy,
        optionalParams.sortOrder,
        optionalParams.gridGroupBy,
        optionalParams.groupByMainGuest,
      );
      // Validate acceptanceStates if provided
      if (optionalParams.acceptanceStates) {
        const validStates = ["ACCEPTED", "DECLINED", "PENDING", "SYSTEM"];
        for (const state of optionalParams.acceptanceStates) {
          if (!validStates.includes(state)) {
            throw new Error("validationError");
          }
        }
      }
    } else if (taskType === "IMPORT_GUESTS_CREATE") {
      sanitizedGuests = optionalParams?.guests?.map((guest) => {
        const sanitized = sanitizeGuest(guest);
        sanitized.eventId = eventId;
        return sanitized;
      });
      optionalParams.guests = sanitizedGuests;
    } else if (taskType === "IMPORT_GUESTS_OVERWRITE") {
      sanitizedGuests = optionalParams?.guests?.map((guest) => {
        const sanitized = sanitizeGuest(guest);
        sanitized.eventId = eventId;
        return sanitized;
      });
      optionalParams.guests = sanitizedGuests;
    } else if (taskType === "EXPORT_SLOTTING_APPOINTMENTS_PDF") {
      // Validate offeringId
      if (!optionalParams.offeringId || !isValidObjectId(optionalParams.offeringId)) {
        throw new Error("validationError");
      }
      // Validate laneIds if provided
      if (optionalParams.laneIds) {
        if (!Array.isArray(optionalParams.laneIds) || optionalParams.laneIds.length > 100) {
          throw new Error("validationError");
        }
        for (const id of optionalParams.laneIds) {
          if (!isValidObjectId(id)) throw new Error("validationError");
        }
      }
      // Validate date formats
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (optionalParams.exportDateStart && !dateRegex.test(optionalParams.exportDateStart)) {
        throw new Error("validationError");
      }
      if (optionalParams.exportDateEnd && !dateRegex.test(optionalParams.exportDateEnd)) {
        throw new Error("validationError");
      }
      // Validate statusFilter
      if (optionalParams.statusFilter) {
        const validStatuses = ["TENTATIVE", "CONFIRMED", "CHECKED_IN", "CANCELLED"];
        for (const s of optionalParams.statusFilter) {
          if (!validStatuses.includes(s)) throw new Error("validationError");
        }
      }
      // Validate groupBy
      if (optionalParams.groupBy && !["day", "lane"].includes(optionalParams.groupBy)) {
        throw new Error("validationError");
      }
      // Validate layoutStyle
      if (optionalParams.layoutStyle && !["detailed", "compact"].includes(optionalParams.layoutStyle)) {
        throw new Error("validationError");
      }
    } else if (
      taskType === "EXPORT_SLOTTING_REQUESTS_TO_EXCEL" ||
      taskType === "EXPORT_SLOTTING_REQUESTS_TO_CSV"
    ) {
      // Slotting-request grid export. offeringId required; column list +
      // archived scope passed through to the Worker.
      if (!optionalParams.offeringId || !isValidObjectId(optionalParams.offeringId)) {
        throw new Error("validationError");
      }
      if (
        optionalParams.slottingRequestColumns &&
        !Array.isArray(optionalParams.slottingRequestColumns)
      ) {
        throw new Error("validationError");
      }
      if (
        optionalParams.archivedScope &&
        !["active", "archived", "all"].includes(optionalParams.archivedScope)
      ) {
        throw new Error("validationError");
      }
    } else if (taskType === "SEND_EMAILS") {
      for (const whitelistId of optionalParams.whitelist ?? []) {
        if (!isValidObjectId(whitelistId))
          throw new Error("Invalid whitelist ID: " + whitelistId);
      }

      for (const blacklistId of optionalParams.blacklist ?? []) {
        if (!isValidObjectId(blacklistId))
          throw new Error("Invalid blacklist ID: " + blacklistId);
      }

      if (!isValidObjectId(optionalParams.emailTemplateId ?? ""))
        throw new Error("Invalid emailTemplateId");

      // Conditions lane (OR-tree recipient filter): shape guard only — the
      // backend normalizes keys + validates operators at submit (400 on a
      // malformed tree), the consumer compiles it fail-closed.
      if (
        optionalParams.conditions !== undefined &&
        (typeof optionalParams.conditions !== "object" ||
          optionalParams.conditions === null ||
          Array.isArray(optionalParams.conditions))
      ) {
        throw new Error("validationError");
      }

      // Send class: a closed enum, and ORGANISATIONAL only with the operator's
      // attestation. Refused before the wire rather than left to the backend's
      // 400 so a malformed caller fails loudly in the cockpit too.
      if (
        optionalParams.sendClass !== undefined &&
        optionalParams.sendClass !== "MARKETING" &&
        optionalParams.sendClass !== "ORGANISATIONAL"
      ) {
        throw new Error("validationError");
      }
      if (
        optionalParams.sendClassAttested !== undefined &&
        typeof optionalParams.sendClassAttested !== "boolean"
      ) {
        throw new Error("validationError");
      }
      if (
        optionalParams.sendClass === "ORGANISATIONAL" &&
        optionalParams.sendClassAttested !== true
      ) {
        throw new Error("validationError");
      }
      // The attestation's provenance: only alongside an attested
      // ORGANISATIONAL, short plain strings (an i18n key, a BCP-47 tag).
      const provenance = [
        optionalParams.sendClassAttestedTextKey,
        optionalParams.sendClassAttestedLocale,
      ];
      if (provenance.some((v) => v !== undefined)) {
        if (optionalParams.sendClass !== "ORGANISATIONAL") {
          throw new Error("validationError");
        }
        if (
          typeof optionalParams.sendClassAttestedTextKey !== "string" ||
          !/^[A-Za-z0-9._-]{1,120}$/.test(optionalParams.sendClassAttestedTextKey) ||
          typeof optionalParams.sendClassAttestedLocale !== "string" ||
          !/^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8}){0,3}$/.test(optionalParams.sendClassAttestedLocale)
        ) {
          throw new Error("validationError");
        }
      }
    } else if (taskType === "EXPORT_SEATING_PDF") {
      // Validate roomIds if provided
      if (optionalParams.roomIds) {
        if (!Array.isArray(optionalParams.roomIds) || optionalParams.roomIds.length > 100) {
          throw new Error("validationError");
        }
        for (const id of optionalParams.roomIds) {
          if (!isValidObjectId(id)) throw new Error("validationError");
        }
      }
      // Validate seatStatusFilter
      if (optionalParams.seatStatusFilter) {
        const validStatuses = ["assigned", "tentative", "reserved", "checkedIn", "unassigned"];
        for (const s of optionalParams.seatStatusFilter) {
          if (!validStatuses.includes(s)) throw new Error("validationError");
        }
      }
      // Validate seatingGroupBy
      if (optionalParams.seatingGroupBy && !["room", "furniture", "status"].includes(optionalParams.seatingGroupBy)) {
        throw new Error("validationError");
      }
      // Enum options. `undefined` = "let the consumer default"; anything else
      // must be a member of the shared unions (parity fixture in
      // src/lib/seating/__tests__). The backend 400s the same values, but a
      // refused submit that never leaves the browser is a cheaper "no".
      const enumOf = <T extends string>(v: unknown, allowed: readonly T[]) =>
        v === undefined || (typeof v === "string" && (allowed as readonly string[]).includes(v));
      if (!enumOf(optionalParams.paperSize, SEATING_PAPER_SIZES)) throw new Error("validationError");
      if (!enumOf(optionalParams.orientation, SEATING_ORIENTATIONS)) throw new Error("validationError");
      if (!enumOf(optionalParams.seatingMode, SEATING_MODES)) throw new Error("validationError");
      if (!enumOf(optionalParams.detailPages, SEATING_DETAIL_PAGES)) throw new Error("validationError");
      if (!enumOf(optionalParams.colourBy, SEATING_COLOUR_BY)) throw new Error("validationError");
      // Flags: absent or a real boolean. A string "false" is truthy on the
      // worker, so it must not pass as a flag.
      for (const flag of [
        optionalParams.includeFloorPlan,
        optionalParams.includeGuestList,
        optionalParams.includeStatsSummary,
        optionalParams.includeUnassignedSeats,
        optionalParams.includeGuestIndex,
        optionalParams.includeColourLegend,
        optionalParams.posterTiling,
      ]) {
        if (flag !== undefined && typeof flag !== "boolean") throw new Error("validationError");
      }
      // Guest columns: an array of ≤ 20 non-empty strings.
      if (optionalParams.guestFields !== undefined) {
        const gf = optionalParams.guestFields;
        if (
          !Array.isArray(gf) ||
          gf.length > SEATING_GUEST_FIELDS_MAX ||
          gf.some((f) => typeof f !== "string" || f.trim() === "")
        ) {
          throw new Error("validationError");
        }
      }
      // Header labels: a plain object of strings (never an array, never null).
      if (optionalParams.columnLabels !== undefined) {
        const cl = optionalParams.columnLabels;
        if (
          typeof cl !== "object" ||
          cl === null ||
          Array.isArray(cl) ||
          Object.values(cl).some((v) => typeof v !== "string")
        ) {
          throw new Error("validationError");
        }
      }
    } else if (taskType === "BULK_UPDATE_GUESTS_BY_FILTER") {
      // Filter-mode bulk edit. `bulkUpdates` is required and must have at
      // least one defined editable field. `excludeIds` (if present) must
      // be valid ObjectIds. `filters` may be empty (= "apply to every
      // matching guest"); the heavy-task worker delegates to
      // `resolveGuestIdsByFilter` which treats empty filters as the
      // all-guests set. Backend's `hasAnyBulkUpdate` re-checks the
      // payload server-side, so this is a cheap UX guard.
      if (!optionalParams.bulkUpdates || !isNonEmptyUpdates(optionalParams.bulkUpdates)) {
        throw new Error("validationError");
      }
      for (const id of optionalParams.excludeIds ?? []) {
        if (!isValidObjectId(id)) throw new Error("validationError");
      }
    } else if (taskType === "BULK_UPDATE_GUESTS_BY_IDS") {
      // Ids-mode bulk edit. `guestIds` required + non-empty + 10k cap
      // (mirrors EXPORT_SPECIFIC_GUESTS_* cap); each entry a valid
      // ObjectId. `bulkUpdates` required + non-empty.
      if (!Array.isArray(optionalParams.guestIds))
        throw new Error("validationError");
      if (optionalParams.guestIds.length === 0)
        throw new Error("validationError");
      if (optionalParams.guestIds.length > 10000)
        throw new Error("validationError");
      for (const id of optionalParams.guestIds) {
        if (!isValidObjectId(id)) throw new Error("validationError");
      }
      optionalParams.guestIds = [...new Set(optionalParams.guestIds)];
      if (!optionalParams.bulkUpdates || !isNonEmptyUpdates(optionalParams.bulkUpdates)) {
        throw new Error("validationError");
      }
    } else if (taskType === "BATCH_CHECK_IN_GUESTS") {
      // Batch check-in / check-out / reset. `filters` may be empty — the
      // service method appends archived=false + the checkedIn precondition
      // itself before resolving the matching set. `excludeIds` (un-ticked
      // banner rows) + `bulkSearch` (active search term) narrow the
      // resolved set — same pass-through the bulk-update tasks use.
      //
      // Exactly one of the two shapes is accepted:
      //   - direction: `batchCheckIn` an explicit boolean, no reset flag
      //   - reset:     `batchCheckInReset === true`, `batchCheckIn` ABSENT
      // The reset shape must not carry a direction. A backend that predates
      // the reset ignores the unknown flag, so a boolean riding along would
      // silently turn a repair into a mass check-in/-out mid-rollout;
      // without it that backend refuses the job instead.
      if (optionalParams.batchCheckInReset !== undefined) {
        if (
          optionalParams.batchCheckInReset !== true ||
          optionalParams.batchCheckIn !== undefined
        ) {
          throw new Error("validationError");
        }
      } else if (typeof optionalParams.batchCheckIn !== "boolean") {
        throw new Error("validationError");
      }
      for (const id of optionalParams.excludeIds ?? []) {
        if (!isValidObjectId(id)) throw new Error("validationError");
      }
    } else if (taskType === "EXPORT_EVENT_CONFIG_TO_JSON") {
      // A-22 — the configuration export takes NO selection: the Worker reads
      // the whole event by id and the sanitizer decides what leaves. Anything
      // beyond the two transport fields is refused rather than ignored, so a
      // caller cannot believe a filter narrowed the file.
      const allowed = new Set(["clientRequestId", "exportFileName"]);
      for (const k of Object.keys(optionalParams)) {
        if (!allowed.has(k)) throw new Error("validationError");
      }
    } else if (taskType === "SEND_SMS_MANUAL") {
      // SMS manual send (plan "Contract and identity"): the dialog builds this
      // body itself, so only the shape is guarded here — the backend resolves
      // the recipients, the text and every limit at submit. The key is
      // REQUIRED for this task type (a keyless submit is the backend's 400):
      // without one a lost answer could never be retried as the same request.
      for (const id of [...(optionalParams.whitelist ?? []), ...(optionalParams.blacklist ?? [])]) {
        if (!isValidObjectId(id)) throw new Error("validationError");
      }
      if (typeof optionalParams.clientRequestId !== "string") throw new Error("validationError");
      if (optionalParams.selectAll !== undefined && typeof optionalParams.selectAll !== "boolean") {
        throw new Error("validationError");
      }
      const hasTemplate = optionalParams.smsTemplateId !== undefined;
      const hasBody = optionalParams.smsBody !== undefined;
      if (hasTemplate === hasBody) throw new Error("validationError");
      if (hasTemplate && !isValidObjectId(optionalParams.smsTemplateId ?? "")) throw new Error("validationError");
      if (hasBody && typeof optionalParams.smsBody !== "string") throw new Error("validationError");
      if (
        typeof optionalParams.confirmedReachable !== "number" ||
        !Number.isInteger(optionalParams.confirmedReachable) ||
        optionalParams.confirmedReachable < 0
      ) {
        throw new Error("validationError");
      }
    } else if (taskType === "EXPORT_ATTACHMENTS_TO_ZIP") {
      // A-22 (6b) — the attachment ZIP takes no guest selection either; the
      // only narrowing is by attachment slot. Anything else is refused, so a
      // caller cannot believe a guest filter narrowed the archive.
      const allowed = new Set(["clientRequestId", "attachmentIds"]);
      for (const k of Object.keys(optionalParams)) {
        if (!allowed.has(k)) throw new Error("validationError");
      }
      const slotIds: unknown = optionalParams.attachmentIds;
      if (slotIds !== undefined) {
        if (!Array.isArray(slotIds) || slotIds.length > 200) throw new Error("validationError");
        for (const id of slotIds) {
          if (typeof id !== "string" || !isValidObjectId(id)) throw new Error("validationError");
        }
      }
    } else {
      throw new Error("Unsupported task type");
    }

    // Validate guestIds if provided
    if (optionalParams.guestIds && optionalParams.guestIds.length > 0) {
      const invalidIds = optionalParams.guestIds.filter(
        (id) => !isValidObjectId(id),
      );
      if (invalidIds.length > 0) {
        throw new Error("Invalid guest IDs provided");
      }
    }

    // Validate filters if provided
    if (optionalParams.filters && optionalParams.filters.length > 0) {
      for (const filter of optionalParams.filters) {
        if (!filter.key || !filter.operator) {
          throw new Error("Invalid filter: key and operator are required");
        }
      }
    }

    // No cookie company on an EVENT job: the server books the job against the
    // company that OWNS the event. The login company's id was wrong on a
    // foreign event and pointless on an own one.
    //
    // `opts` is forwarded only when given, so every existing caller's client
    // call keeps its exact three-argument shape.
    onTheWire = true;
    return opts
      ? await submitBackgroundJobApi(eventId, taskType, optionalParams, opts)
      : await submitBackgroundJobApi(eventId, taskType, optionalParams);
  } catch (error) {
    console.error("Error submitting background job:", error);
    const serverError = await handleErrorServer(error);
    return onTheWire ? serverError : { ...serverError, local: true };
  }
}

/**
 * Submit a company-scope job (EXPORT_EVENTS_* tasks fired from the root dashboard).
 * Companion to {@link submitBackgroundJob} — posts to /company/{companyId}.
 *
 * Resolves companyId from the `companyId` cookie (same source other handlers use
 * — see BillingHandling, CompanyHandling). Returns both the jobId and the scopeId
 * (companyId) so the client can register the job with BackgroundJobsProvider
 * without having to duplicate the cookie read.
 */
export async function submitBackgroundJobCompany(
  taskType: string,
  optionalParams: OptionalParamsBackgroundJobs,
): Promise<{ jobId: string; scopeId: string } | ServerError> {
  try {
    if (!taskType || taskType.trim() === "") {
      throw new Error("Task type is required");
    }

    const companyId = await getCookie("companyId");
    if (!companyId || !isValidObjectId(companyId)) {
      throw new Error("Invalid companyId");
    }

    if (
      taskType === "EXPORT_SPECIFIC_EVENTS_TO_EXCEL" ||
      taskType === "EXPORT_SPECIFIC_EVENTS_TO_CSV"
    ) {
      if (!Array.isArray(optionalParams.eventIds)) throw new Error("validationError");
      if (optionalParams.eventIds.length === 0) throw new Error("validationError");
      if (optionalParams.eventIds.length > 10000) throw new Error("validationError");
      for (const eventId of optionalParams.eventIds) {
        if (!isValidObjectId(eventId)) throw new Error("validationError");
      }
      optionalParams.eventIds = [...new Set(optionalParams.eventIds)];
    } else if (taskType === "CONTACT_IMPORT_CREATE") {
      // CRM contact import — mirrors the IMPORT_GUESTS_CREATE payload gate on
      // the event path: rows must be a non-empty array of plain objects. Field
      // sanitization/validation stays server-side (shared validators against
      // the company field schema — no DataType handling here).
      if (!Array.isArray(optionalParams.contacts))
        throw new Error("validationError");
      if (optionalParams.contacts.length === 0)
        throw new Error("validationError");
      for (const contact of optionalParams.contacts) {
        if (!contact || typeof contact !== "object" || Array.isArray(contact))
          throw new Error("validationError");
      }
    }

    if (optionalParams.filters && optionalParams.filters.length > 0) {
      for (const filter of optionalParams.filters) {
        if (!filter.key || !filter.operator) {
          throw new Error("Invalid filter: key and operator are required");
        }
      }
    }

    optionalParams.companyId = companyId;

    const jobId = await submitBackgroundJobCompanyApi(
      companyId,
      taskType,
      optionalParams,
    );
    return { jobId, scopeId: companyId };
  } catch (error) {
    console.error("Error submitting company-scope background job:", error);
    return handleErrorServer(error);
  }
}

export async function getBackgroundJobStatus(
  eventId: string,
  backgroundJobId: string,
  fields: string = "backgroundJobId,eventId,taskType,status,progress,createdAt,updatedAt",
): Promise<BackgroundJob | ServerError> {
  try {
    if (!isValidObjectId(backgroundJobId)) {
      throw new Error("Invalid backgroundJobId");
    }
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid eventId");
    }

    // Validate fields parameter
    if (fields && typeof fields !== "string") {
      throw new Error("Invalid fields parameter");
    }
    if (fields && fields.length > 500) {
      throw new Error("Fields parameter is too long");
    }


    return await getBackgroundJobStatusApi(eventId, backgroundJobId, fields);
  } catch (error) {
    console.error("Error getting background job status:", error);
    return handleErrorServer(error);
  }
}

export async function getBackgroundJobStatusCompany(
  companyId: string,
  backgroundJobId: string,
  fields: string = "backgroundJobId,eventId,taskType,status,progress,createdAt,updatedAt",
): Promise<BackgroundJob | ServerError> {
  try {
    if (!isValidObjectId(backgroundJobId)) {
      throw new Error("Invalid backgroundJobId");
    }
    if (!isValidObjectId(companyId)) {
      throw new Error("Invalid companyId");
    }
    if (fields && typeof fields !== "string") {
      throw new Error("Invalid fields parameter");
    }
    if (fields && fields.length > 500) {
      throw new Error("Fields parameter is too long");
    }

    return await getBackgroundJobStatusCompanyApi(companyId, backgroundJobId, fields);
  } catch (error) {
    console.error("Error getting company background job status:", error);
    return handleErrorServer(error);
  }
}

export async function deleteBackgroundJob(
  eventId: string,
  backgroundJobId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(backgroundJobId)) {
      throw new Error("Invalid backgroundJobId");
    }
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid eventId");
    }

    await deleteBackgroundJobApi(eventId, backgroundJobId);
  } catch (error) {
    console.error("Error deleting background job:", error);
    return handleErrorServer(error);
  }
}

export async function deleteBackgroundJobCompany(
  companyId: string,
  backgroundJobId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(backgroundJobId)) {
      throw new Error("Invalid backgroundJobId");
    }
    if (!isValidObjectId(companyId)) {
      throw new Error("Invalid companyId");
    }

    await deleteBackgroundJobCompanyApi(companyId, backgroundJobId);
  } catch (error) {
    console.error("Error deleting company background job:", error);
    return handleErrorServer(error);
  }
}

export async function cancelBackgroundJob(
  eventId: string,
  backgroundJobId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(backgroundJobId)) {
      throw new Error("Invalid backgroundJobId");
    }
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid eventId");
    }

    await cancelBackgroundJobApi(eventId, backgroundJobId);
  } catch (error) {
    console.error("Error cancelling background job:", error);
    return handleErrorServer(error);
  }
}

export async function cancelBackgroundJobCompany(
  companyId: string,
  backgroundJobId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(backgroundJobId)) {
      throw new Error("Invalid backgroundJobId");
    }
    if (!isValidObjectId(companyId)) {
      throw new Error("Invalid companyId");
    }

    await cancelBackgroundJobCompanyApi(companyId, backgroundJobId);
  } catch (error) {
    console.error("Error cancelling company background job:", error);
    return handleErrorServer(error);
  }
}

/**
 * Validate common export options
 */
function validateExportOptions(fields?: string, filters?: Filter[]) {
  // Validate fields if provided
  if (fields !== undefined) {
    if (typeof fields !== "string") {
      throw new Error("validationError");
    }
    if (fields.length > 1000) {
      throw new Error("validationError");
    }
  }

  // Validate filters
  if (filters !== undefined) {
    if (!Array.isArray(filters)) {
      throw new Error("validationError");
    }
    // Limit number of filters
    if (filters.length > 50) {
      throw new Error("validationError");
    }

    // Validate each filter
    for (const filter of filters) {
      if (
        !filter.key ||
        typeof filter.key !== "string" ||
        filter.key.length > 100
      ) {
        throw new Error("validationError");
      }
      if (
        filter.value !== undefined &&
        filter.value !== null &&
        typeof filter.value !== "string" &&
        typeof filter.value !== "number" &&
        typeof filter.value !== "boolean"
      ) {
        throw new Error("validationError");
      }
      if (filter.operator && typeof filter.operator !== "string") {
        throw new Error("validationError");
      }
    }
  }
}

/**
 * Validate grid-export sort + group params before they ship to the
 * background-job submit endpoint. Mirrors the Java
 * {@code ExportSortGroupValidation} key pattern + length cap; the
 * per-entity allowlist enforcement happens server-side. This pass is
 * a UX safeguard (catches typos earlier) and a defense-in-depth gate
 * against `__proto__` / operator-injection keys.
 */
function validateSortGroupOptions(
  sortBy?: string,
  sortOrder?: string,
  gridGroupBy?: string,
  groupByMainGuest?: boolean,
) {
  const KEY_RE = /^(additionalFields\.)?[A-Za-z][A-Za-z0-9_]{0,99}$/;
  const check = (name: string, v: string | undefined) => {
    if (v === undefined || v === null || v === "") return;
    if (typeof v !== "string" || v.length > 100) {
      throw new Error("validationError");
    }
    if (!KEY_RE.test(v)) {
      throw new Error("validationError");
    }
  };
  check("sortBy", sortBy);
  check("gridGroupBy", gridGroupBy);
  if (
    sortOrder !== undefined &&
    sortOrder !== null &&
    sortOrder !== "" &&
    sortOrder !== "asc" &&
    sortOrder !== "desc"
  ) {
    throw new Error("validationError");
  }
  if (groupByMainGuest !== undefined && typeof groupByMainGuest !== "boolean") {
    throw new Error("validationError");
  }
}
